import React, { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { FileManager, FileCacheProvider } from '@hiyve/react-collaboration';
import { HiyveProvider } from '@hiyve/react';
import { useApi } from '../../../hooks/useApi';
import { useAppUser } from '../../../hooks/useAppUser';

// Mirrors muzieRTC/src/Utils.js::cleanUserId so the FileManager's
// per-row ownership check (`item.userId === userId`) can match the
// way public-file records are keyed on the server.
function cleanUserId(value) {
  if (!value || typeof value !== 'string') return '';
  return value.replace(/[^-_@a-zA-Z0-9]/g, '_').toLowerCase();
}

const PUBLIC_FILES_KEY = ['admin', 'public-files'];

function AdminFileManager() {
  const api = useApi();
  const queryClient = useQueryClient();
  const { user } = useAppUser();
  // Public-file records were uploaded by whichever admin happened to
  // create them, so their per-record `userId` rarely matches the
  // currently-viewing admin. We pass `isItemOwner={() => true}` to
  // the SDK FileManager so tenant-owners can rename / delete / move
  // every public record regardless of original uploader. Server-side
  // authorisation (requireUserMatch + tenant scoping in
  // /api/public-files) is the real permission boundary; this prop
  // only unlocks the UI affordances.
  const isItemOwner = useCallback(() => true, []);
  const currentUserId = user?.email ? cleanUserId(user.email) : undefined;

  const { data, isLoading, error } = useQuery({
    queryKey: PUBLIC_FILES_KEY,
    queryFn: () => api.get('/api/public-files'),
  });

  const files = useMemo(() => data?.files ?? [], [data]);
  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: PUBLIC_FILES_KEY });
  }, [queryClient]);

  const onUploadFile = useCallback(async (file, location) => {
    const { url, fileId, s3Key } = await api.post('/api/public-files/upload-url', {
      fileName: file.name,
      contentType: file.type || 'application/octet-stream',
    });

    const putRes = await fetch(url, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
    });
    if (!putRes.ok) {
      throw new Error(`S3 upload failed: ${putRes.status}`);
    }

    return api.post('/api/public-files', {
      fileId,
      s3Path: s3Key,
      fileName: file.name,
      location: location || '/',
      contentType: file.type || 'application/octet-stream',
    });
  }, [api]);

  const onDeleteFile = useCallback(async (fileId) => {
    await api.delete(`/api/public-files/${encodeURIComponent(fileId)}`);
    return true;
  }, [api]);

  const onRenameFile = useCallback(async (fileId, newName) => {
    await api.patch(`/api/public-files/${encodeURIComponent(fileId)}`, { fileName: newName });
    return true;
  }, [api]);

  const onMoveFile = useCallback(async (fileId, newPath) => {
    await api.patch(`/api/public-files/${encodeURIComponent(fileId)}`, { location: newPath });
    return true;
  }, [api]);

  const onCreateFolder = useCallback(async (name, parentPath) => {
    await api.post('/api/public-files/folders', { name, parentPath });
    return true;
  }, [api]);

  const onDeleteFolder = useCallback(async (path) => {
    await api.delete('/api/public-files/folders', { path });
    return true;
  }, [api]);

  const onGetFileUrl = useCallback(async (file) => {
    const { url } = await api.get(`/api/public-files/${encodeURIComponent(file.fileId)}/signed-url`);
    return url;
  }, [api]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>Public Files</Typography>
        <Alert severity={error.status === 403 ? 'warning' : 'error'}>
          {error.message || 'Failed to load public files'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" gutterBottom>Public Files</Typography>
        <Typography variant="body2" sx={{
          color: "text.secondary"
        }}>
          Tenant-wide public file library. Files here are visible to any user in your
          organization that holds the <code>public-files</code> role.
        </Typography>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        {/*
          Render the FileManager in its default (private-files) scope so the
          writable UI (upload / rename / delete / create-folder) is available.
          The SDK hard-codes public-files scope to read-only; "public" here is a
          server-side property of the underlying FileIndex records, not a UI mode.
          All callbacks target /api/public-files/* which keeps the data scoped
          to `scope: 'public'`.
        */}
        {/*
          FileManager internally calls @hiyve/react's useUserFiles() hook
          unconditionally (unlike its sibling useRoom/useParticipants/useClient
          calls, which are wrapped in safe fallbacks). useUserFiles() throws
          "Hiyve hooks must be used within a HiyveProvider" when no store
          context exists. We run FileManager in controlled mode (all data comes
          from FileCacheProvider props), so we wrap the subtree in a bare
          HiyveProvider purely to supply an empty store — its userFiles slice is
          unused here. No room is joined and no cloud token is requested.
        */}
        <HiyveProvider>
          <FileCacheProvider
            files={files}
            onUploadFile={onUploadFile}
            onDeleteFile={onDeleteFile}
            onRenameFile={onRenameFile}
            onMoveFile={onMoveFile}
            onCreateFolder={onCreateFolder}
            onDeleteFolder={onDeleteFolder}
            onGetFileUrl={onGetFileUrl}
            onRefresh={refresh}
          >
            <FileManager
              showToolbar
              showBreadcrumbs
              userId={currentUserId}
              isItemOwner={isItemOwner}
            />
          </FileCacheProvider>
        </HiyveProvider>
      </Box>
    </Box>
  );
}

export default AdminFileManager;
